import { Box } from '@mui/material';
import { CheckBox, Table } from '@vxui/components';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ETranscoderProfileKind,
  ETranscoderProfileType,
  TranscoderAudioProfile,
  TranscoderProfile,
  useDeleteTranscoderAudioProfileMutation,
} from '@vxui/common';
import { tableStyle } from '../profilesTable.styles';
import { AudioTableProps } from '../profilesTable.types';
import { useActionModal } from '../modals/useActionModal';
import { getColumns } from './audioTable.helpers';
import { filter } from '../profilesTable.helpers';
import { ExpandProfile } from '../components/ExpandProfile';
import { skipNewRowSorting } from '../profilesTable.sorting';
import { useTemplateFilter } from '../templateFilter.helpers';
import { SubheaderContainer } from '../components/SubheaderContainer';

const rowDisabledCriteria = (row: TranscoderAudioProfile) => row.type === ETranscoderProfileType.new;

export const AudioTable = forwardRef(
  (
    {
      data,
      actions,
      singleProfileToDelete,
      isLoading,
      editableRows,
      onColumnChange,
      onSelectChange,
      onSingleCloseDeleteHandler,
    }: AudioTableProps,
    ref?,
  ) => {
    const { t } = useTranslation();
    const columns = getColumns({
      t,
      actions,
      onChangeHandler: onColumnChange,
      onSelectChangeHandler: onSelectChange,
      editableRows,
    });
    const [templateFilterState, templates] = useTemplateFilter(data);
    const [templateName] = templateFilterState;

    const [filterText, setFilterText] = useState('');
    const [deleteAudioProfile] = useDeleteTranscoderAudioProfileMutation();
    const rows: TranscoderAudioProfile[] = data ? data.filter(filter(filterText.toLowerCase(), templateName)) : [];
    const selectProps = { label: null, indeterminate: (isIndeterminate: boolean) => isIndeterminate };

    const { selectedProfiles, toggledClearRows, onSelectedRowsChangeHandler, subHeaderActions, components } =
      useActionModal({
        kind: ETranscoderProfileKind.audio,
        profileToDelete: singleProfileToDelete,
        deleteAction: deleteAudioProfile,
        onCloseDeleteModalHandler: onSingleCloseDeleteHandler,
      });

    return (
      <Box ref={ref} sx={tableStyle}>
        {components.singleDelete}
        {components.bulkDelete}
        <Table
          id="transcoder-audio-profile"
          className="profiles-table"
          fixedHeader
          fixedHeaderScrollHeight="calc(100vh - 320px)"
          paginationResetDefaultPage={true}
          dense
          columns={columns}
          data={rows}
          progressPending={isLoading}
          selectableRows
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          selectableRowsComponent={CheckBox}
          selectableRowsComponentProps={selectProps}
          selectableRowDisabled={rowDisabledCriteria}
          onSelectedRowsChange={onSelectedRowsChangeHandler}
          expandableRows={true}
          expandableRowsComponent={props => <ExpandProfile profile={props.data} />}
          expandableRowDisabled={(row: TranscoderProfile) => !row.templates || row.templates?.length === 0}
          subHeader
          sortFunction={skipNewRowSorting}
          subHeaderComponent={
            <SubheaderContainer
              filterText={filterText}
              setFilterText={setFilterText}
              subHeaderActions={subHeaderActions}
              templateFilterState={templateFilterState}
              templates={templates}
              selectedProfiles={selectedProfiles}
            />
          }
          clearSelectedRows={toggledClearRows}
        />
      </Box>
    );
  },
);
